/**
 * ConciergePersonaModal — edit the tenant's Concierge persona.
 *
 * Deliberately the same editor as the Agent-of-Agents one (AoAConfigModal):
 * the regular Edit-Agent modal shell from BotModal.css — a custom
 * `.modal-overlay > .modal` with a header, scrollable `.modal-tab-content` and
 * a `.modal-actions` footer — plus the shared `ExpandableTextarea`. An admin
 * who has configured the meta-agent should recognise this on sight.
 *
 * Opened from the gear in the Concierge page header, and only rendered for
 * group_admin / super_admin (the routes are gated the same way). Editing
 * applies to EVERY user in the tenant, and — when published — to tenants
 * beneath it, so the modal states that plainly rather than burying it.
 *
 * THREE MODES, exactly as AoA has them:
 *   inherit  — follow the persona a parent tenant published.
 *   shipped  — run one of the packaged personas (platform default, JewishPro).
 *              A real decision, distinct from `inherit`: it overrides a parent.
 *   custom   — this tenant's own text.
 * Switching modes NEVER deletes the custom text; it stays saved for switching
 * back. That is the whole point of the mode switch.
 *
 * Only the persona is editable. The per-turn operating brief (working set,
 * pre-search results, the search-first workflow, the output rules) is always
 * appended by the backend and cannot be changed here — that is what keeps a
 * customised Concierge still searching and still inside the user's permitted
 * agents. The modal says so, so nobody has to discover it by breaking it.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import { ExpandableTextarea } from '@/components/common';
import { conciergeApi, type ConciergeConfig } from '../services/conciergeApi';
// Reuse the regular Edit-Agent modal styles so this matches the AoA editor 1:1.
import '@modules/chatbots/components/BotModal.css';
// The publish toggle reuses `.context-switch` rather than adding a fifth copy
// of the same switch; TenantContextEditor reuses it across modules the same way.
import '@modules/chatbots/components/BotContextFiles.css';
import { DefaultModelPicker } from '@/components/common/DefaultModelPicker';
import './ConciergePersonaModal.css';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The organization's Concierge model slot after a default-model change was
   *  saved, so the page shows it on the next conversation without a reload. */
  onModelSaved?: (model: NonNullable<ConciergeConfig['model']>) => void;
}

type PersonaMode = 'inherit' | 'shipped' | 'custom';

export const ConciergePersonaModal: React.FC<Props> = ({ open, onClose, onModelSaved }) => {
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<ConciergeConfig | null>(null);

  // `personaText` holds ONLY the tenant's custom text. The other modes render
  // read-only previews, so a mode switch never touches what the admin wrote.
  const [personaText, setPersonaText] = useState('');
  const [mode, setMode] = useState<PersonaMode>('shipped');
  const [preset, setPreset] = useState('default');
  const [publishDown, setPublishDown] = useState(false);
  // The model the Concierge starts a conversation on (a family key; null =
  // platform default). Same storage as the super-admin Meta-agents tab.
  const [defaultModel, setDefaultModel] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await conciergeApi.getConfig();
      setConfig(cfg);
      // "Inherit" is only a real mode when there is something to inherit. The
      // platform root, and any tenant whose ancestors publish nothing, stores
      // `inherit` but actually runs the shipped default — show it that way
      // rather than an empty pane with no mode selected.
      if (cfg.persona_source === 'inherit' && cfg.inheritable_from) {
        setMode('inherit');
      } else {
        setMode(cfg.system_prompt && !cfg.use_default_persona ? 'custom' : 'shipped');
      }
      setPreset(cfg.persona_preset || 'default');
      setPersonaText(cfg.system_prompt ?? '');
      setPublishDown(cfg.publish_to_descendants ?? false);
      setDefaultModel(cfg.model && !cfg.model.is_default ? cfg.model.default_model : null);
    } catch {
      message.error(t('concierge.persona.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const inheritable = config?.inheritable_from ?? null;
  const reach = config?.descendant_reach ?? { following: 0, overriding: 0 };
  const hasDescendants = reach.following + reach.overriding > 0;
  const presets = config?.presets ?? [];
  const presetOf = (key: string) => presets.find((p) => p.key === key);
  // The platform default is just another shipped edition; it is not repeated
  // as its own payload field.
  const presetText = (key: string): string =>
    presetOf(key)?.system_prompt ?? presetOf('default')?.system_prompt ?? '';
  // A publishing ancestor with no text of its own standardised its subtree on
  // the platform default, so preview that rather than an empty box.
  const inheritedPreview = inheritable
    ? (inheritable.system_prompt ?? presetText('default'))
    : '';
  // Descriptions are product copy: prefer the locale file, fall back to the
  // label the server shipped so a new edition is never nameless.
  const presetLabel = (p: { key: string; label: string }) =>
    t(`concierge.persona.preset.${p.key}.label`, { defaultValue: p.label });
  const presetDesc = (key: string) => {
    const p = presetOf(key);
    return p ? t(`concierge.persona.preset.${key}.description`, { defaultValue: p.description }) : '';
  };

  const switchMode = (next: PersonaMode) => {
    setMode(next);
    // First switch to custom with nothing saved yet: seed the editor from
    // whatever is live today, so the admin starts from a working persona
    // rather than a blank box.
    if (next === 'custom' && !personaText.trim()) {
      setPersonaText(inheritedPreview || presetText(preset));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await conciergeApi.saveConfig({
        // The custom text is ALWAYS persisted (even while a shipped persona is
        // live) so a mode switch is never destructive. An EMPTY STRING clears
        // the stored text — not null, which the route cannot tell apart from
        // "field omitted" and would therefore treat as "leave it alone",
        // silently keeping a persona the admin just deleted.
        system_prompt: personaText.trim() ? personaText : '',
        persona_source: mode === 'inherit' ? 'inherit' : 'local',
        use_default_persona: mode === 'shipped',
        persona_preset: preset,
        // An inheriting tenant has nothing of its own to hand down.
        publish_to_descendants: mode === 'inherit' ? false : publishDown,
      });
      // The default model rides its own endpoint (the super-admin tab's key),
      // only when it actually changed.
      const storedDefault = config?.model && !config.model.is_default ? config.model.default_model : null;
      if (defaultModel !== storedDefault) {
        saved.model = await conciergeApi.updateDefaultModel(defaultModel);
        onModelSaved?.(saved.model);
      }
      setConfig(saved);
      message.success(t('concierge.persona.saved'));
      onClose();
    } catch {
      message.error(t('concierge.persona.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const readOnly = !config?.can_edit;

  const footer = (
    <div className="modal-actions">
      <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>
        {t('common.cancel')}
      </button>
      <button
        type="button"
        className="btn-primary"
        onClick={save}
        disabled={saving || loading || readOnly}
        data-testid="concierge-persona-save"
      >
        {saving ? (
          <span className="loading-indicator">
            <div className="loading-spinner" />
            <span>{t('common.updating')}</span>
          </span>
        ) : (
          <span>{t('common.save')}</span>
        )}
      </button>
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal" data-testid="concierge-persona-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-content">
            <h2 className="modal-title">{t('concierge.persona.title')}</h2>
            <p className="modal-subtitle">{t('concierge.persona.lead')}</p>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading || !config ? (
          <div className="modal-tab-content">
            <div className="loading-state">
              <div className="loading-spinner" />
              <p>{t('common.loading')}</p>
            </div>
          </div>
        ) : (
          <div className="modal-tab-content cpm-body">
            {readOnly && (
              <div className="cpm-banner cpm-banner--muted">
                {t('concierge.persona.readOnly')}
              </div>
            )}

            {/* Default model — the family the Concierge starts a conversation
                on. One storage with the super-admin Meta-agents tab; the
                allowed list stays the super admin's. */}
            <div className="form-group" data-testid="concierge-default-model">
              <label className="form-label">{t('models.defaultModel.label')}</label>
              <DefaultModelPicker
                value={defaultModel}
                onChange={setDefaultModel}
                allowedModels={config?.model?.allowed_models?.length ? config.model.allowed_models : 'all'}
                disabled={readOnly || saving}
                unsetHint={t('models.defaultModel.unsetPlatform')}
                help={t('concierge.persona.defaultModelHint')}
                testId="concierge-default-model-picker"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('concierge.persona.personaLabel')}</label>
              <p className="form-help-text cpm-persona-help">
                {t('concierge.persona.personaHint')}
              </p>

              <div className="cpm-modes" role="tablist">
                {inheritable && (
                  <button
                    type="button"
                    className={`cpm-mode-btn${mode === 'inherit' ? ' cpm-mode-btn--active' : ''}`}
                    onClick={() => switchMode('inherit')}
                    aria-pressed={mode === 'inherit'}
                    data-testid="concierge-persona-mode-inherit"
                  >
                    {t('concierge.persona.modeInherit')}
                  </button>
                )}
                <button
                  type="button"
                  className={`cpm-mode-btn${mode === 'shipped' ? ' cpm-mode-btn--active' : ''}`}
                  onClick={() => switchMode('shipped')}
                  aria-pressed={mode === 'shipped'}
                  data-testid="concierge-persona-mode-default"
                >
                  {t('concierge.persona.modeDefault')}
                </button>
                <button
                  type="button"
                  className={`cpm-mode-btn${mode === 'custom' ? ' cpm-mode-btn--active' : ''}`}
                  onClick={() => switchMode('custom')}
                  aria-pressed={mode === 'custom'}
                  data-testid="concierge-persona-mode-custom"
                >
                  {t('concierge.persona.modeCustom')}
                </button>
              </div>

              {/* Where the running persona comes from, named explicitly so an
                  admin is never confused about why the text isn't theirs. */}
              {config.persona_origin === 'inherited' && mode === 'inherit' && (
                <div className="cpm-banner" data-testid="concierge-inherit-banner">
                  {t('concierge.persona.inheritedFrom', {
                    tenant: config.persona_origin_tenant_name ?? '',
                  })}
                </div>
              )}
              {inheritable && mode !== 'inherit' && (
                <div className="cpm-banner cpm-banner--muted">
                  {t('concierge.persona.overridingAncestor', { tenant: inheritable.tenant_name })}
                </div>
              )}

              {mode === 'inherit' ? (
                <>
                  <ExpandableTextarea
                    value={inheritedPreview}
                    onChange={() => undefined}
                    rows={10}
                    label={t('concierge.persona.personaLabel')}
                    disabled
                  />
                  <div className="cpm-actions">
                    <span className="cpm-status">
                      {t('concierge.persona.usingInherited', {
                        tenant: inheritable?.tenant_name ?? '',
                      })}
                      {personaText.trim() ? ` ${t('concierge.persona.customSaved')}` : ''}
                    </span>
                  </div>
                </>
              ) : mode === 'shipped' ? (
                <>
                  {/* WHICH shipped persona. This is the "switch between the
                      defaults" half of the mode switch. */}
                  {presets.length > 1 && (
                  <div className="cpm-presets" data-testid="concierge-persona-presets">
                    {presets.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        className={`cpm-preset-btn${preset === p.key ? ' cpm-preset-btn--active' : ''}`}
                        onClick={() => setPreset(p.key)}
                        aria-pressed={preset === p.key}
                        title={presetDesc(p.key)}
                        data-testid={`concierge-persona-preset-${p.key}`}
                        disabled={readOnly}
                      >
                        {presetLabel(p)}
                      </button>
                    ))}
                  </div>
                  )}
                  <ExpandableTextarea
                    value={presetText(preset)}
                    onChange={() => undefined}
                    rows={10}
                    label={t('concierge.persona.personaLabel')}
                    disabled
                  />
                  <div className="cpm-actions">
                    <span className="cpm-status">
                      {presetDesc(preset)}
                      {personaText.trim() ? ` ${t('concierge.persona.customSaved')}` : ''}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <ExpandableTextarea
                    value={personaText}
                    onChange={setPersonaText}
                    placeholder={presetText('default')}
                    rows={10}
                    label={t('concierge.persona.personaLabel')}
                    disabled={readOnly}
                    id="concierge-persona-textarea"
                  />
                  <div className="cpm-actions">
                    <span className="cpm-status">{t('concierge.persona.usingCustom')}</span>
                    {/* Start from a shipped edition instead of a blank page —
                        the fastest way to adopt JewishPro and then tweak it. */}
                    {presets.length > 1 && (
                    <div className="cpm-buttons">
                      <span className="cpm-status">{t('concierge.persona.startFrom')}</span>
                      {presets.map((p) => (
                        <button
                          key={p.key}
                          type="button"
                          className="btn-secondary"
                          onClick={() => setPersonaText(presetText(p.key))}
                          disabled={readOnly || personaText === presetText(p.key)}
                          data-testid={`concierge-persona-seed-${p.key}`}
                        >
                          {presetLabel(p)}
                        </button>
                      ))}
                    </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Hand this persona down the tenant tree. Only meaningful when
                this tenant decides for itself and has tenants beneath it. */}
            {hasDescendants && mode !== 'inherit' && (
              <div className="form-group cpm-publish-group">
                <div className="cpm-publish-toggle">
                  <span className="cpm-toggle-label">{t('concierge.persona.publish')}</span>
                  <label className="context-switch">
                    <input
                      type="checkbox"
                      checked={publishDown}
                      onChange={() => setPublishDown((v) => !v)}
                      disabled={readOnly}
                      data-testid="concierge-persona-publish"
                    />
                    <span className="context-switch-slider" />
                  </label>
                </div>
                <p className="form-help-text">{t('concierge.persona.publishHint')}</p>
                <p className="form-help-text cpm-reach">
                  {t('concierge.persona.publishReach', {
                    following: reach.following,
                    overriding: reach.overriding,
                  })}
                </p>
              </div>
            )}

            <p className="cpm-foot">{t('concierge.persona.mechanicsNote')}</p>
          </div>
        )}
        {/* Outside the scrolling body so Save/Cancel stay reachable on a short
            screen instead of scrolling away below a long persona. */}
        {footer}
      </div>
    </div>
  );
};

export default ConciergePersonaModal;
